/**
 * Secure Storage Utility
 * Encrypts data before storing in localStorage and decrypts when retrieving
 * Uses the same AES-256-GCM encryption as API calls
 */

import { encrypt, decrypt } from './encryption';

/**
 * Securely store data in localStorage with encryption
 * @param {string} key - The localStorage key
 * @param {any} value - The value to store (will be encrypted)
 */
export async function setSecureItem(key, value) {
  try {
    // Encrypt the value
    const encryptedValue = await encrypt(value);
    // Store encrypted data
    localStorage.setItem(key, encryptedValue);
  } catch (error) {
    console.error(`Failed to securely store item "${key}":`, error);
    throw new Error(`Secure storage failed: ${error.message}`);
  }
}

/**
 * Retrieve and decrypt data from localStorage
 * @param {string} key - The localStorage key
 * @returns {any} - The decrypted value, or null if not found
 */
export async function getSecureItem(key) {
  try {
    const encryptedValue = localStorage.getItem(key);

    // Return null if item doesn't exist
    if (!encryptedValue) {
      return null;
    }

    // Decrypt and return the value
    const decryptedValue = await decrypt(encryptedValue);
    return decryptedValue;
  } catch (error) {
    console.error(`Failed to retrieve secure item "${key}":`, error);
    // Return null instead of throwing to handle corrupted data gracefully
    return null;
  }
}

/**
 * Remove an item from localStorage
 * @param {string} key - The localStorage key
 */
export function removeSecureItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to remove item "${key}":`, error);
  }
}

/**
 * Clear all items from localStorage
 */
export function clearSecureStorage() {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Failed to clear secure storage:', error);
  }
}

/**
 * Check if a key exists in localStorage
 * @param {string} key - The localStorage key
 * @returns {boolean} - True if key exists
 */
export function hasSecureItem(key) {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error(`Failed to check item "${key}":`, error);
    return false;
  }
}

/**
 * Get all keys from localStorage
 * @returns {string[]} - Array of all localStorage keys
 */
export function getSecureKeys() {
  try {
    return Object.keys(localStorage);
  } catch (error) {
    console.error('Failed to get storage keys:', error);
    return [];
  }
}

/**
 * Store multiple items securely
 * @param {Object} items - Object with key-value pairs to store
 */
export async function setSecureItems(items) {
  try {
    const promises = Object.entries(items).map(([key, value]) => setSecureItem(key, value));
    await Promise.all(promises);
  } catch (error) {
    console.error('Failed to store multiple items:', error);
    throw new Error(`Secure storage failed: ${error.message}`);
  }
}

/**
 * Retrieve multiple items securely
 * @param {string[]} keys - Array of keys to retrieve
 * @returns {Object} - Object with key-value pairs
 */
export async function getSecureItems(keys) {
  try {
    const promises = keys.map(async (key) => {
      const value = await getSecureItem(key);
      return [key, value];
    });

    const entries = await Promise.all(promises);
    return Object.fromEntries(entries);
  } catch (error) {
    console.error('Failed to retrieve multiple items:', error);
    return {};
  }
}
